import { useState, useEffect, useMemo } from "react";
import useOrders from "../../api/orders";
import useCompany from "../../api/company";
import {
  Download,
  RefreshCw,
  ShoppingCart,
  Settings2,
  CreditCard,
  CheckCircle,
  XCircle,
  ExternalLink,
  Unlink,
  ChevronDown,
  Building2,
  Archive,
} from "lucide-react";
import * as XLSX from "xlsx";
import OrderStats from "./orders/OrderStats";
import OrderFilters from "./orders/OrderFilters";
import OrderTable from "./orders/OrderTable";
import OrderDetails from "./orders/OrderDetails";
import { useTheme } from "../../context/ThemeContext";
import { useAuth, getAuthToken } from "../../context/AuthContext";
import MollieService from "../../api/mollie";

const OrderManager = () => {
  const { getAllOrders, getAdminOrders, updateOrderStatus, loading } =
    useOrders();
  const { getAllCompanies } = useCompany();
  const { company } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const isAdmin = company?.isAdmin === 1;

  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [sourceFilter, setSourceFilter] = useState("all"); // "all" | "shop" | "configurator"

  // Admin company selector
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false);

  // Archived orders (persisted to localStorage)
  const [archivedIds, setArchivedIds] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("archived_orders") || "[]");
    } catch {
      return [];
    }
  });

  const handleArchive = (orderId) => {
    const id = String(orderId);
    setArchivedIds((prev) => {
      const next = prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [...prev, id];
      localStorage.setItem("archived_orders", JSON.stringify(next));
      return next;
    });
  };

  // Mollie connection state
  const [mollieConnected, setMollieConnected] = useState(false);
  const [mollieLoading, setMollieLoading] = useState(false);
  const [mollieError, setMollieError] = useState(null);
  const [mollieData, setMollieData] = useState(null);
  const [showApiKeyInput, setShowApiKeyInput] = useState(false);
  const [apiKeyValue, setApiKeyValue] = useState("");
  const [apiKeySaving, setApiKeySaving] = useState(false);

  // Filter & Sort State
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    startDate: "",
    endDate: "",
  });
  const [sortConfig, setSortConfig] = useState({
    key: "date",
    direction: "desc",
  });

  const fetchOrders = async (companyId) => {
    try {
      let data;
      if (isAdmin && companyId) {
        data = await getAdminOrders(companyId, { includeConfigurator: true });
      } else {
        data = await getAllOrders({ includeConfigurator: true });
      }
      setOrders(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch orders", error);
    }
  };

  useEffect(() => {
    fetchOrders(selectedCompanyId);
    checkMollieConnection();
    if (isAdmin) {
      getAllCompanies()
        .then((data) => setCompanies(Array.isArray(data) ? data : []))
        .catch(() => {});
    }
  }, []);

  const checkMollieConnection = async () => {
    try {
      const token = getAuthToken();
      if (token && company?.id) {
        const conn = await MollieService.checkConnection(token, company.id);
        if (conn) {
          setMollieConnected(true);
          const info = await MollieService.getInfo(token);
          if (info) setMollieData(info);
        }
      }
    } catch (e) {
      // silently ignore
    }
  };

  const connectMollie = async () => {
    setMollieLoading(true);
    setMollieError(null);
    try {
      const token = getAuthToken();
      const { url } = await MollieService.getAuthorizeUrl(token);
      window.location.href = url;
    } catch (error) {
      setMollieError("Kon geen verbinding maken met Mollie.");
      setMollieLoading(false);
    }
  };

  const disconnectMollie = async () => {
    try {
      const token = getAuthToken();
      await MollieService.disconnect(token, company?.id);
      setMollieConnected(false);
      setMollieData(null);
    } catch (error) {
      console.error("Mollie disconnection error:", error);
    }
  };

  const saveMollieApiKey = async () => {
    if (!apiKeyValue.trim()) return;
    setApiKeySaving(true);
    setMollieError(null);
    try {
      const token = getAuthToken();
      await MollieService.saveApiKey(token, apiKeyValue.trim());
      setApiKeyValue("");
      setShowApiKeyInput(false);
      setMollieConnected(true);
      const info = await MollieService.getInfo(token);
      if (info) setMollieData(info);
    } catch (error) {
      setMollieError(error.message || "Kon de API-key niet opslaan.");
    } finally {
      setApiKeySaving(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    // Configurator orders start with "cfg-", skip status update via orders API
    if (String(id).startsWith("cfg-")) return;
    try {
      await updateOrderStatus(id, newStatus);
      setOrders((prev) =>
        prev.map((o) => (o.id === id ? { ...o, status: newStatus } : o)),
      );
      if (selectedOrder && selectedOrder.id === id) {
        setSelectedOrder((prev) => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      console.error("Failed to update status", error);
      fetchOrders(selectedCompanyId);
    }
  };

  const handleExport = () => {
    const exportData = filteredAndSortedOrders.map((order) => ({
      ID: order.id,
      Bron: order.source === "configurator" ? "Configurator" : "Webshop",
      Datum: new Date(order.created_at).toLocaleDateString(),
      Klant: `${order.customer_details?.firstName} ${order.customer_details?.lastName}`,
      Email: order.customer_details?.email,
      Adres: [
          (order.customer_details?.address?.street ?? order.customer_details?.address) || "",
          order.customer_details?.address?.houseNumber || "",
          (order.customer_details?.address?.postalCode ?? order.customer_details?.zip) || "",
          (order.customer_details?.address?.city ?? order.customer_details?.city) || "",
        ].filter(Boolean).join(" "),
      Totaal: order.total_amount,
      Status: order.status,
      Betaling: order.payment_provider,
      Producten: order.items?.map((i) => `${i.quantity}x ${i.name}`).join(", "),
      Configurator: order.configurator_name || "",
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Orders");
    XLSX.writeFile(
      wb,
      `orders_export_${new Date().toISOString().split("T")[0]}.xlsx`,
    );
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Filter and Sort Logic
  const filteredAndSortedOrders = useMemo(() => {
    let result = [...orders];

    // Archive / source filter
    if (sourceFilter === "archived") {
      result = result.filter((o) => archivedIds.includes(String(o.id)));
    } else {
      result = result.filter((o) => !archivedIds.includes(String(o.id)));
      if (sourceFilter === "shop") {
        result = result.filter((o) => o.source !== "configurator");
      } else if (sourceFilter === "configurator") {
        result = result.filter((o) => o.source === "configurator");
      }
    }

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter((order) => {
        const customerName =
          `${order.customer_details?.firstName} ${order.customer_details?.lastName}`.toLowerCase();
        const email = (order.customer_details?.email || "").toLowerCase();
        const id = String(order.id);
        const confName = (order.configurator_name || "").toLowerCase();
        return (
          customerName.includes(searchLower) ||
          email.includes(searchLower) ||
          id.includes(searchLower) ||
          confName.includes(searchLower)
        );
      });
    }

    if (filters.status) {
      result = result.filter((order) => order.status === filters.status);
    }

    if (filters.startDate) {
      const start = new Date(filters.startDate).setHours(0, 0, 0, 0);
      result = result.filter(
        (order) => new Date(order.created_at).getTime() >= start,
      );
    }

    if (filters.endDate) {
      const end = new Date(filters.endDate).setHours(23, 59, 59, 999);
      result = result.filter(
        (order) => new Date(order.created_at).getTime() <= end,
      );
    }

    // Sort
    result.sort((a, b) => {
      if (!sortConfig.key) return 0;

      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (sortConfig.key === "customer") {
        aValue =
          `${a.customer_details?.firstName} ${a.customer_details?.lastName}`.toLowerCase();
        bValue =
          `${b.customer_details?.firstName} ${b.customer_details?.lastName}`.toLowerCase();
      } else if (sortConfig.key === "date") {
        aValue = new Date(a.created_at).getTime();
        bValue = new Date(b.created_at).getTime();
      } else if (sortConfig.key === "total_amount") {
        aValue = parseFloat(a.total_amount);
        bValue = parseFloat(b.total_amount);
      } else if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return result;
  }, [orders, filters, sortConfig, sourceFilter, archivedIds]);

  const configuratorOrderCount = orders.filter(
    (o) => o.source === "configurator" && !archivedIds.includes(String(o.id)),
  ).length;

  const selectedCompany = companies.find((c) => c.id === selectedCompanyId);

  const handleSelectCompany = (id) => {
    setSelectedCompanyId(id);
    setCompanyDropdownOpen(false);
    fetchOrders(id);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2
            className={`text-[15px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
          >
            Bestellingen
          </h2>
          <p
            className={`text-[12px] mt-0.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
          >
            Een overzicht van al je bestellingen en statistieken.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => fetchOrders(selectedCompanyId)}
            disabled={loading}
            className={`h-8 px-3 rounded-lg text-[12px] font-medium flex items-center gap-1.5 border transition-colors ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
          >
            <RefreshCw
              className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`}
            />
            Verversen
          </button>
          <button
            onClick={handleExport}
            className={`h-8 px-3 rounded-lg text-[12px] font-medium flex items-center gap-1.5 transition-colors ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
          >
            <Download className="h-3.5 w-3.5" /> Exporteren
          </button>
        </div>
      </div>

      {/* Source tabs */}
      <div
        className={`flex gap-1 p-1 rounded-lg w-fit ${isDark ? "bg-white/[0.04]" : "bg-gray-100"}`}
      >
        {[
          { key: "all", label: "Alle bestellingen", icon: null },
          { key: "shop", label: "Webshop", icon: ShoppingCart },
          {
            key: "configurator",
            label: `Configurator${configuratorOrderCount > 0 ? ` (${configuratorOrderCount})` : ""}`,
            icon: Settings2,
          },
          {
            key: "archived",
            label: `Archief${archivedIds.length > 0 ? ` (${orders.filter((o) => archivedIds.includes(String(o.id))).length || ""})` : ""}`,
            icon: Archive,
          },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setSourceFilter(key)}
            className={`h-7 px-3 rounded-md text-[12px] font-medium flex items-center gap-1.5 transition-colors ${
              sourceFilter === key
                ? isDark
                  ? "bg-white/[0.1] text-white"
                  : "bg-white text-gray-900 shadow-sm"
                : isDark
                  ? "text-gray-500 hover:text-gray-300"
                  : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {Icon && <Icon className="h-3 w-3" />}
            {label}
          </button>
        ))}
      </div>

      {/* Admin company selector */}
      {isAdmin && companies.length > 0 && (
        <div className="relative w-fit">
          <button
            onClick={() => setCompanyDropdownOpen((prev) => !prev)}
            className={`h-8 px-3 rounded-lg text-[12px] font-medium flex items-center gap-2 border transition-colors ${
              isDark
                ? "border-white/[0.08] text-gray-300 hover:bg-white/[0.04] bg-white/[0.02]"
                : "border-gray-200 text-gray-700 hover:bg-gray-50 bg-white"
            }`}
          >
            <Building2 className="h-3.5 w-3.5 text-gray-400" />
            {selectedCompany ? selectedCompany.name : "Eigen bestellingen"}
            <ChevronDown className="h-3 w-3 text-gray-400" />
          </button>
          {companyDropdownOpen && (
            <div
              className={`absolute left-0 top-10 z-50 min-w-[200px] rounded-xl shadow-xl border py-1 ${
                isDark
                  ? "bg-[#141414] border-white/[0.08]"
                  : "bg-white border-gray-200"
              }`}
            >
              <button
                onClick={() => handleSelectCompany(null)}
                className={`w-full text-left px-3 py-2 text-[12px] transition-colors ${
                  !selectedCompanyId
                    ? isDark
                      ? "bg-white/[0.06] text-white"
                      : "bg-gray-100 text-gray-900"
                    : isDark
                      ? "text-gray-400 hover:bg-white/[0.04] hover:text-white"
                      : "text-gray-600 hover:bg-gray-50"
                }`}
              >
                Eigen bestellingen
              </button>
              {companies.map((c) => (
                <button
                  key={c.id}
                  onClick={() => handleSelectCompany(c.id)}
                  className={`w-full text-left px-3 py-2 text-[12px] transition-colors ${
                    selectedCompanyId === c.id
                      ? isDark
                        ? "bg-white/[0.06] text-white"
                        : "bg-gray-100 text-gray-900"
                      : isDark
                        ? "text-gray-400 hover:bg-white/[0.04] hover:text-white"
                        : "text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Mollie connection banner */}
      <div
        className={`rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${
          isDark
            ? "bg-white/[0.03] border border-white/[0.06]"
            : "bg-white border border-gray-200/80"
        }`}
      >
        <div className="flex items-center gap-3">
          <div
            className={`w-9 h-9 rounded-lg flex items-center justify-center ${
              isDark ? "bg-white/[0.04]" : "bg-orange-50"
            }`}
          >
            <CreditCard className="h-4 w-4 text-orange-500" />
          </div>
          <div>
            <p
              className={`text-[13px] font-semibold ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              Mollie Betalingen
            </p>
            <p
              className={`text-[11px] ${
                isDark ? "text-gray-600" : "text-gray-500"
              }`}
            >
              {mollieConnected && mollieData?.organization
                ? mollieData.organization.name
                : "Koppel je Mollie account om betalingen te verwerken"}
            </p>
          </div>
          <span
            className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ml-1 ${
              mollieConnected
                ? isDark
                  ? "bg-emerald-500/15 text-emerald-400"
                  : "bg-emerald-50 text-emerald-700"
                : isDark
                  ? "bg-white/[0.04] text-gray-500"
                  : "bg-gray-100 text-gray-500"
            }`}
          >
            {mollieConnected ? (
              <CheckCircle className="h-3 w-3" />
            ) : (
              <XCircle className="h-3 w-3" />
            )}
            {mollieConnected
              ? mollieData?.mode === "apikey"
                ? "Verbonden via API-key"
                : "Verbonden"
              : "Niet verbonden"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {mollieError && (
            <span
              className={`text-[11px] ${isDark ? "text-red-400" : "text-red-600"}`}
            >
              {mollieError}
            </span>
          )}
          {mollieConnected ? (
            <button
              onClick={disconnectMollie}
              className={`h-8 px-3 rounded-lg text-[12px] font-medium inline-flex items-center gap-1.5 transition-colors ${
                isDark
                  ? "border border-red-500/20 text-red-400 hover:bg-red-500/10"
                  : "border border-red-200 text-red-600 hover:bg-red-50"
              }`}
            >
              <Unlink className="h-3 w-3" /> Verbreken
            </button>
          ) : (
            <>
              <button
                onClick={() => setShowApiKeyInput((v) => !v)}
                className={`h-8 px-3 rounded-lg text-[12px] font-medium inline-flex items-center gap-1.5 transition-colors ${
                  isDark
                    ? "border border-white/10 text-gray-300 hover:bg-white/5"
                    : "border border-gray-200 text-gray-700 hover:bg-gray-50"
                }`}
              >
                API-key gebruiken
              </button>
              <button
                onClick={connectMollie}
                disabled={mollieLoading}
                className="h-8 px-3 rounded-lg text-[12px] font-medium inline-flex items-center gap-1.5 transition-colors bg-orange-600 text-white hover:bg-orange-700"
              >
                {mollieLoading ? (
                  <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <ExternalLink className="h-3.5 w-3.5" />
                )}
                {mollieLoading ? "Verbinden..." : "Koppel met Mollie"}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Mollie API-key invoer (permanente koppeling, verloopt nooit) */}
      {showApiKeyInput && !mollieConnected && (
        <div
          className={`rounded-xl p-4 space-y-3 ${
            isDark
              ? "bg-white/[0.03] border border-white/[0.06]"
              : "bg-white border border-gray-200/80"
          }`}
        >
          <p
            className={`text-[13px] font-medium ${isDark ? "text-white" : "text-gray-900"}`}
          >
            Koppel permanent met je Mollie Live API-key
          </p>
          <p
            className={`text-[12px] leading-relaxed ${isDark ? "text-gray-500" : "text-gray-500"}`}
          >
            Zo vind je de key: log in op{" "}
            <a
              href="https://my.mollie.com"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              my.mollie.com
            </a>{" "}
            → kies je website-profiel → <strong>Developers</strong> →{" "}
            <strong>API-keys</strong> → kopieer de <strong>Live API-key</strong>{" "}
            (begint met <code>live_</code>). Deze koppeling verloopt nooit en
            hoeft nooit opnieuw verbonden te worden.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="password"
              value={apiKeyValue}
              onChange={(e) => setApiKeyValue(e.target.value)}
              placeholder="live_xxxxxxxxxxxxxxxxxxxxxxxxxx"
              autoComplete="off"
              className={`flex-1 h-9 px-3 rounded-lg text-[13px] font-mono outline-none transition-colors ${
                isDark
                  ? "bg-black/40 border border-white/10 text-white placeholder:text-gray-700 focus:border-white/25"
                  : "bg-white border border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-gray-400"
              }`}
            />
            <button
              onClick={saveMollieApiKey}
              disabled={apiKeySaving || !apiKeyValue.trim()}
              className="h-9 px-4 rounded-lg text-[12px] font-medium inline-flex items-center justify-center gap-1.5 transition-colors bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50"
            >
              {apiKeySaving ? (
                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <CheckCircle className="h-3.5 w-3.5" />
              )}
              {apiKeySaving ? "Controleren..." : "Opslaan & testen"}
            </button>
          </div>
          <p
            className={`text-[11px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
          >
            De key wordt eerst live bij Mollie gevalideerd voor hij wordt
            opgeslagen.
          </p>
        </div>
      )}

      <OrderStats orders={filteredAndSortedOrders} />

      <div className="space-y-3">
        <OrderFilters filters={filters} setFilters={setFilters} />
        <OrderTable
          orders={filteredAndSortedOrders}
          onSort={handleSort}
          sortConfig={sortConfig}
          onArchive={handleArchive}
          archivedIds={archivedIds}
          onViewDetails={(order) => {
            setSelectedOrder(order);
            setIsDetailsOpen(true);
          }}
        />
      </div>

      <OrderDetails
        order={selectedOrder}
        isOpen={isDetailsOpen}
        onClose={() => setIsDetailsOpen(false)}
        onStatusChange={handleStatusChange}
      />
    </div>
  );
};

export default OrderManager;
